import { Button, Form, notification, Pagination, Popconfirm, Table, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { CheckOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageTitle from '../../components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import Filter, {
  OVERTIME_REQUEST_STATUS,
  OVERTIME_STATUS_LABEL,
  OvertimeFilterFormValue,
} from './component/filter';
import { formatData } from 'utils/common';
import moment from 'moment';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { refreshPendingCounts } from 'layouts/pending-counts';
import { DEPARTMENT_LABEL } from 'pages/star-shared/labels';
import {
  LinkedFilterTag,
  toEnumValue,
  toPositiveInt,
  useDropSearchParams,
  useParamChange,
} from 'pages/user-manager/helpers/user-deep-link';

const LIMIT = 20;

const initFilter = {
  page: 1,
  limit: LIMIT,
};

const formatHours = (minutes: unknown) => {
  if (minutes === null || minutes === undefined || minutes === '') return '';
  const value = Number(minutes);
  return Number.isFinite(value) ? Number((value / 60).toFixed(2)) : '';
};

export interface OvertimeFilter {
  limit: number;
  page: number;
  status?: OVERTIME_REQUEST_STATUS;
  from?: string;
  to?: string;
  userId?: number;
  assignedApproverId?: number;
  hasAssignedApprover?: boolean;
  batchId?: number;
}

/** Liên kết sâu: ?userId=<id>&name=<tên> và/hoặc ?status=PENDING|APPROVED|REJECTED|CANCELLED. */
function readDeepLink(params: URLSearchParams) {
  const userId = toPositiveInt(params.get('userId'));
  const status = toEnumValue(OVERTIME_REQUEST_STATUS, params.get('status'));
  if (!userId && !status) return null;
  const filter: OvertimeFilter = formatData({ ...initFilter, userId, status });
  const preset: Partial<OvertimeFilterFormValue> = {
    status,
    userId: userId ? { value: userId, label: params.get('name') || `#${userId}` } : undefined,
  };
  return { filter, preset };
}

const OvertimeManager = () => {
  const [loading, setLoading] = useState(true);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [searchParams] = useSearchParams();
  const dropSearchParams = useDropSearchParams();
  const [deepLink] = useState(() => readDeepLink(searchParams));
  const [filterForm] = Form.useForm<OvertimeFilterFormValue>();
  const linkedUserId = toPositiveInt(searchParams.get('userId'));
  const linkedUserLabel = searchParams.get('name') || `#${linkedUserId}`;
  const [filter, setFilter] = useState<OvertimeFilter>(deepLink?.filter || initFilter);
  const { isAdmin: canApproveAsAdmin } = useCurrentRoles();

  const onFinish = (value: Partial<OvertimeFilter>) => {
    setFilter((preState) => formatData({ ...preState, ...value, page: 1 }));
    // Chọn người khác / Xoá lọc → bỏ ?userId= cũ khỏi URL để F5 không lọc lại người cũ.
    if (linkedUserId && value.userId !== linkedUserId) dropSearchParams('userId', 'name');
  };

  // URL đổi sau khi trang đã mở (đóng thẻ "Đang lọc", bấm lại menu, Back/Forward).
  useParamChange(linkedUserId, (next, prev) => {
    if (next) {
      setFilter((p) => ({ ...p, userId: next, page: 1 }));
      filterForm.setFieldValue('userId', { value: next, label: linkedUserLabel });
      return;
    }
    setFilter((p) => (p.userId === prev ? formatData({ ...p, userId: undefined, page: 1 }) : p));
    if (filterForm.getFieldValue('userId')?.value === prev) {
      filterForm.setFieldValue('userId', undefined);
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.overtimeRequests, {
          ...filter,
        });
        const items = res?.data?.data || res?.data?.items || res?.data || [];
        const total = res?.data?.metadata?.total || res?.data?.meta?.total || res?.data?.total || 0;
        setDataResponse({ items, totalCount: total });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filter]);

  const handleApproveOvertime = async (id: number) => {
    try {
      await AxiosUtils.patch(`${API.overtimeRequests}/${id}/approve`);
      notification.success({ message: 'Đã duyệt đơn tăng ca.' });
      setFilter((prev) => ({ ...prev }));
      refreshPendingCounts();
    } catch (error: any) {
      // Interceptor ném ra body lỗi của API ({ message }), không phải Error.
      const raw = error?.message ?? error;
      const message = Array.isArray(raw) ? raw.join(', ') : typeof raw === 'string' ? raw : '';
      notification.error({
        message: 'Không thể duyệt đơn tăng ca.',
        description: message || 'Vui lòng thử lại sau.',
      });
    }
  };

  const statusColor = useMemo(
    () =>
      ({
        [OVERTIME_REQUEST_STATUS.PENDING]: 'gold',
        [OVERTIME_REQUEST_STATUS.APPROVED]: 'green',
        [OVERTIME_REQUEST_STATUS.REJECTED]: 'red',
        [OVERTIME_REQUEST_STATUS.CANCELLED]: 'default',
      } as Record<string, string>),
    []
  );

  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Nhân viên',
      key: 'user',
      width: 170,
      render: (record) => (
        <Tooltip title={record?.user?.email ?? ''}>
          <span>
            {record?.user?.username ?? ''}{' '}
            {record?.user?.department
              ? `(${DEPARTMENT_LABEL[record.user.department] || record.user.department})`
              : ''}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Bắt đầu',
      dataIndex: 'startAt',
      key: 'startAt',
      width: 160,
      render: (value) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {value ? moment(value).format('YYYY-MM-DD HH:mm') : ''}
        </span>
      ),
    },
    {
      title: 'Kết thúc',
      dataIndex: 'endAt',
      key: 'endAt',
      width: 160,
      render: (value) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {value ? moment(value).format('YYYY-MM-DD HH:mm') : ''}
        </span>
      ),
    },
    {
      title: 'Giờ',
      dataIndex: 'minutes',
      key: 'minutes',
      width: 90,
      render: (value) => <span>{formatHours(value)}</span>,
    },
    {
      title: 'Lý do',
      dataIndex: 'reason',
      key: 'reason',
      width: 220,
      render: (value, record) => (
        <div>
          <Tooltip title={value}>
            <div>{value}</div>
          </Tooltip>
          {record?.note && (
            <Tooltip title={record.note}>
              <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 4 }}>{record.note}</div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (value: string) => (
        <Tag
          color={statusColor[value] || 'default'}
          style={{ borderRadius: 6, padding: '2px 8px' }}
        >
          {OVERTIME_STATUS_LABEL[value as OVERTIME_REQUEST_STATUS] || value}
        </Tag>
      ),
    },
    {
      title: 'Người duyệt',
      key: 'assignedApprover',
      width: 130,
      render: (record) => (
        <Tooltip title={record?.assignedApprover?.email ?? ''}>
          <span>{record?.assignedApprover?.username ?? ''}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Đợt gửi',
      key: 'batch',
      width: 150,
      render: (record) => (
        <span>
          {record?.batch?.id ? `#${record.batch.id}` : ''}{' '}
          {record?.batch?.status
            ? `(${
                OVERTIME_STATUS_LABEL[record.batch.status as OVERTIME_REQUEST_STATUS] ||
                record.batch.status
              })`
            : ''}
        </span>
      ),
    },
    {
      title: 'Tạo lúc',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (value) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {value ? moment(value).format('YYYY-MM-DD HH:mm') : ''}
        </span>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      // Bảng rộng hơn khung (cuộn ngang) → ghim cột thao tác bên phải để nút Duyệt luôn thấy.
      fixed: 'right' as const,
      width: 110,
      render: (_, record) =>
        canApproveAsAdmin && record?.status === OVERTIME_REQUEST_STATUS.PENDING ? (
          <Popconfirm
            title="Duyệt đơn tăng ca này?"
            okText="Duyệt"
            cancelText="Huỷ"
            onConfirm={() => handleApproveOvertime(record?.id)}
          >
            <Button type="text" icon={<CheckOutlined />} title="Duyệt" aria-label="Duyệt" />
          </Popconfirm>
        ) : null,
    },
  ];

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  return (
    <div>
      <PageTitle title="Tăng ca" />
      <div style={{ marginBottom: 24 }}>
        <Filter form={filterForm} preset={deepLink?.preset} onSubmit={onFinish} />
      </div>
      {linkedUserId && filter.userId === linkedUserId && (
        <LinkedFilterTag
          label={linkedUserLabel}
          onClose={() => dropSearchParams('userId', 'name')}
        />
      )}
      <div className="custom-table custom-form">
        <Table
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={dataResponse.items}
          scroll={{ x: 1560 }}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <Pagination
            showSizeChanger={false}
            defaultCurrent={LIMIT}
            current={filter.page}
            onChange={handleChangePage}
            showLessItems
            total={dataResponse.totalCount}
            pageSize={LIMIT}
          />
        )}
      </div>
    </div>
  );
};

export default OvertimeManager;
